import {
  TABLE_SORTER_TOOLTIP,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty } from '../../../shared/ui/quiet-states.js';
// Platform admin mail queue screen (archetype: DataView, cf. AdminUsersPage).
//
// Search, status, kind and period filters are all server-side; pagination is
// server-side too. The status summary chips show GLOBAL counts (same numbers
// as the dashboard) and double as one-click status filters. Row retry is
// offered only for failed/bounced; a row click opens the detail drawer. The
// letter body (bodyText) is never shown in P2 — minimal PII (plan §7.3).
import { useMemo, useState, type ReactElement } from 'react';
import { Button, Table, Tooltip, Typography } from 'antd';
import type { ColumnType, TablePaginationConfig } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { formatDateTime } from '../lib/format.js';
import {
  ADMIN_PERIOD_PRESETS,
  makeAdminPeriodState,
  type AdminPeriodState,
} from '../lib/period.js';
import {
  useAdminOutbox,
  useAdminOutboxDetail,
  useAdminOutboxStatusCounts,
} from '../hooks/useAdminMail.js';
import { useAdminMailMutations } from '../hooks/useAdminMailMutations.js';
import { OutboxStatusBadge } from '../components/OutboxStatusBadge.js';
import { OutboxStatusSummary } from '../components/OutboxStatusSummary.js';
import { AdminOutboxRetryAction } from '../components/AdminOutboxRetryAction.js';
import { AdminMailDrawer } from '../components/AdminMailDrawer.js';
import type { AdminOutboxListItem, OutboxKind, OutboxStatus } from '../api/admin.client.js';

const { Text } = Typography;

type StatusFilter = 'all' | OutboxStatus;
type KindFilter = 'all' | OutboxKind;

const STATUS_FILTER_OPTIONS: readonly StatusFilter[] = [
  'all',
  'pending',
  'sending',
  'sent',
  'delivered',
  'bounced',
  'spam_complaint',
  'failed',
];

const KIND_FILTER_OPTIONS: readonly KindFilter[] = [
  'all',
  'meeting_invited',
  'meeting_updated',
  'meeting_cancelled',
  'workspace_invited',
  'password_reset',
  'email_verification',
  'agent_email',
];

const DEFAULT_PAGE_SIZE = 20;

export function AdminMailPage(): ReactElement {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [period, setPeriod] = useState<AdminPeriodState>(() => makeAdminPeriodState('all'));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const outboxQuery = useAdminOutbox({
    page,
    pageSize,
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(kindFilter !== 'all' ? { kind: kindFilter } : {}),
    ...(activeQuery.trim().length > 0 ? { search: activeQuery.trim() } : {}),
    ...(period.from !== undefined ? { from: period.from } : {}),
  });
  const statusCountsQuery = useAdminOutboxStatusCounts();
  const detailQuery = useAdminOutboxDetail(selectedMessageId);
  const { retryMutation } = useAdminMailMutations();

  const total = outboxQuery.data?.total ?? 0;
  const rows = outboxQuery.data?.messages ?? [];

  const columns = useMemo<Array<ColumnType<AdminOutboxListItem>>>(
    () => [
      {
        title: t('crm.admin.mail.col_subject'),
        key: 'subject',
        render: (_: unknown, row: AdminOutboxListItem) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 13 }} ellipsis>
              {row.subject}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
              {row.workspace.name}
            </Text>
          </div>
        ),
      },
      {
        title: t('crm.admin.mail.col_to'),
        key: 'to',
        width: 220,
        render: (_: unknown, row: AdminOutboxListItem) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text className="font-mono" style={{ fontSize: 12 }} ellipsis>
              {row.toEmail}
            </Text>
            {row.toName !== null ? (
              <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                {row.toName}
              </Text>
            ) : null}
          </div>
        ),
      },
      {
        title: t('crm.admin.mail.col_kind'),
        dataIndex: 'kind',
        key: 'kind',
        width: 150,
        render: (value: OutboxKind) => (
          <Tooltip title={value}>
            <Text style={{ fontSize: 12 }}>{t(`crm.admin.mail.kind.${value}`)}</Text>
          </Tooltip>
        ),
      },
      {
        title: t('crm.admin.mail.col_status'),
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (value: OutboxStatus) => <OutboxStatusBadge status={value} />,
      },
      {
        title: t('crm.admin.mail.col_attempts'),
        dataIndex: 'attempts',
        key: 'attempts',
        width: 100,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono font-semibold" style={{ fontSize: 12 }}>
            {value}
          </Text>
        ),
      },
      {
        title: t('crm.admin.mail.col_available_at'),
        dataIndex: 'availableAt',
        key: 'availableAt',
        width: 140,
        render: (value: string) => (
          <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
            {formatDateTime(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.mail.col_last_error'),
        dataIndex: 'lastError',
        key: 'lastError',
        width: 200,
        render: (value: string | null) =>
          value === null ? (
            <Text disabled style={{ fontSize: 12 }}>
              —
            </Text>
          ) : (
            <Tooltip title={value}>
              <Text type="danger" style={{ fontSize: 12 }} ellipsis>
                {value}
              </Text>
            </Tooltip>
          ),
      },
      {
        title: t('crm.admin.mail.col_sent_at'),
        dataIndex: 'sentAt',
        key: 'sentAt',
        width: 140,
        render: (value: string | null) => (
          <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
            {formatDateTime(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.mail.col_actions'),
        key: 'actions',
        width: 80,
        render: (_: unknown, row: AdminOutboxListItem) => (
          <AdminOutboxRetryAction
            message={row}
            pending={retryMutation.isPending}
            onRetry={(messageId) => retryMutation.mutateAsync(messageId)}
          />
        ),
      },
    ],
    [t, retryMutation],
  );

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: page,
      pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: [20, 30, 50].map(String),
      onChange: (nextPage, nextPageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
      },
    }),
    [page, pageSize, total],
  );

  function applySearch(value: string): void {
    setActiveQuery(value);
    setPage(1);
  }

  if (outboxQuery.isError) {
    return (
      <ErrorState
        title={t('crm.admin.mail.load_error')}
        error={outboxQuery.error}
        onRetry={() => void outboxQuery.refetch()}
      />
    );
  }

  const hasActiveFilters =
    activeQuery.trim().length > 0 ||
    statusFilter !== 'all' ||
    kindFilter !== 'all' ||
    period.from !== undefined;

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={t('crm.admin.mail.title')}
        subtitle={t('crm.admin.mail.subtitle', {
          visible: rows.length,
          total,
        })}
        actions={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              void outboxQuery.refetch();
              void statusCountsQuery.refetch();
            }}
            loading={outboxQuery.isFetching}
            aria-label={t('crm.admin.mail.reload')}
            style={LIST_CREATE_BUTTON_STYLE}
          >
            {t('crm.admin.mail.reload')}
          </Button>
        }
      />

      <div className="flex flex-col gap-3">
        <OutboxStatusSummary
          counts={statusCountsQuery.data?.statuses}
          loading={statusCountsQuery.isLoading}
          activeStatus={statusFilter}
          onSelect={(status) => {
            setStatusFilter(status);
            setPage(1);
          }}
        />

        <FilterBar
          search={
            <FilterSearch
              aria-label={t('crm.admin.mail.search_label')}
              placeholder={t('crm.admin.mail.search_placeholder')}
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onPressEnter={applySearch}
            />
          }
        >
          <FilterSelect
            aria-label={t('crm.admin.mail.status_filter_label')}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as StatusFilter);
              setPage(1);
            }}
            options={STATUS_FILTER_OPTIONS.map((option) => ({
              value: option,
              label:
                option === 'all'
                  ? t('crm.admin.mail.filter_all')
                  : t(`crm.admin.outbox.status_${option}`),
            }))}
          />
          <FilterSelect
            aria-label={t('crm.admin.mail.kind_filter_label')}
            value={kindFilter}
            onChange={(value) => {
              setKindFilter(value as KindFilter);
              setPage(1);
            }}
            options={KIND_FILTER_OPTIONS.map((option) => ({
              value: option,
              label:
                option === 'all'
                  ? t('crm.admin.mail.filter_all')
                  : t(`crm.admin.mail.kind.${option}`),
            }))}
          />
          <FilterSelect
            aria-label={t('crm.admin.common.period_label')}
            value={period.preset}
            onChange={(value) => {
              setPeriod(makeAdminPeriodState(value as AdminPeriodState['preset']));
              setPage(1);
            }}
            options={ADMIN_PERIOD_PRESETS.map((preset) => ({
              value: preset,
              label: t(`crm.admin.common.period_${preset}`),
            }))}
          />
        </FilterBar>

        <div style={LIST_SURFACE_STYLE}>
          <Table<AdminOutboxListItem>
            showSorterTooltip={TABLE_SORTER_TOOLTIP}
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={outboxQuery.isLoading}
            pagination={pagination}
            onRow={(row) => ({
              onClick: () => setSelectedMessageId(row.id),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <QuietEmpty
                  text={
                    hasActiveFilters ? t('crm.admin.mail.empty_search') : t('crm.admin.mail.empty')
                  }
                />
              ),
            }}
          />
        </div>

        <AdminMailDrawer
          open={selectedMessageId !== null}
          messageId={selectedMessageId}
          detail={detailQuery.data}
          isLoading={detailQuery.isLoading}
          onClose={() => setSelectedMessageId(null)}
        />
      </div>
    </div>
  );
}
